import { registerComponent } from "./component";
import { createElement, onEvent, uniqueId } from "./_dom";
import { initPopupContainer, closePopup } from "./_menu";
import { parseColor, lightOrDark, rgbToHsl } from "./colorutils";

function removeIcons(classList) {
	if (typeof classList === "string")
		classList = classList.split(' ').filter(item => !item.startsWith("ui-icon-")).join(' ');
	return classList;
}

function createItems(formId, menu) {
	const items = menu.map((item, i) => {
		const parsedColor = parseColor(item.color);
		const parsedHsl = rgbToHsl(parsedColor);
		const menuItem = createElement('li', {
				role: "none",
				class: "v3-menu__cell"
			}, 
			item.href ?
			createElement('a', {
					role: item.checked === true || item.checked === false ? "menuitemcheckbox" : "menuitem",
					tabindex: i && -1,
					href: item.href,
					class: [
						"v3-menu__item", 
						item.checked === true && "ui-icon-check", 
						item.checked !== true && item["icon-id"] && "ui-icon-custom", 
						item.checked ? removeIcons(item.class) : item.class, 
						parsedColor && `v3-hint-color--${lightOrDark(parsedColor)}`
					],
					"data-form": formId,
					"data-icon": item["icon-id"],
					"data-confirm": item["confirmation"],
					"aria-checked": item.checked === true ? "true" : item.checked === false ? "false": undefined,
					"--v3-hint-color": parsedHsl && `hsl(${parsedHsl.h},${parsedHsl.s}%,${parsedHsl.l}%)`,
					"--v3-hint-color-h": parsedHsl && parsedHsl.h,
					"--v3-hint-color-s": parsedHsl && parsedHsl.s,
					"--v3-hint-color-l": parsedHsl && parsedHsl.l
				},
				item.text
			) :
			createElement("div", { role: "separator", class: item.text === "---" ? "v3-menu__separator" : "v3-menu__header" }, item.text));
		return menuItem;
	});
	return items;
}



function createDropdownMenu(button, toggler, options) {
	const { menu: data, menuClass, appendTo } = options;
	const hasIcons = data.some(item => item.checked || item["icon-id"]) || data.some(item => /\bui-icon-/.test(item.class));
	
	const dropdown = createElement('ul', {
			id: uniqueId("v3-toolaction__menu"),
			class: [ "v3-toolaction__menu v3-menu v3-menu-scrollable v3-popup", menuClass, hasIcons && "v3-menu--hasicons" ],
			role: "menu",
			tabindex: -1,
		},
		...createItems(button.getAttribute("form"), data)
	);

	onEvent(dropdown, ".v3-menu__item", "click", (e, link) => {
		// const confirm = link.dataset.confirm;
		// if (confirm == null || window.confirm(confirm || "Are you sure?")) {
			const newEvent = new CustomEvent("v3-toolaction.menuclick", { cancelable: true, bubbles: true, detail: link });
			button.dispatchEvent(newEvent);
			if (newEvent.defaultPrevented)
				e.preventDefault();
			closePopup(dropdown);
		// }
	});

	let container;
	if (appendTo instanceof Element) {
		container = appendTo;
	} else if (typeof appendTo === "string") {
		switch (appendTo) {
			case "beforebegin":
			case "afterbegin":
			case "beforeend":
			case "afterend":
				toggler.insertAdjacentElement(appendTo, dropdown);
				container = null;
				break;
			case "container":
				container = initPopupContainer();
				break;
			default:
				container = document.querySelector(appendTo);
		}
	} else {
		container = initPopupContainer();
	}
	container?.append(dropdown);
	return dropdown;
}

function createOnButtonClickHandler(defUrl, defConfirm) {
	return function () {
		const button = this;
		if (button.disabled)
			return;
		if (defUrl) {
			if (defConfirm == null || window.confirm(defConfirm ? defConfirm : "Are you sure?")) {
				location.href = defUrl;
			}
		}
	};
}

class ToolAction {
	constructor(button, options = {}) {
		this.button = button;
		this.dropdownToggler = button;
		const menuExtra = button.dataset.menu ? { menu: JSON.parse(button.dataset.menu) } : {};
		this.options = { ...options, ...menuExtra, ...JSON.parse(button.dataset.toolactionOptions || "{}") };
	}

	getOption = (name) => this.options[name];
	setOption = (name, value) => this.options[name] = value;

	#attachMenuHandlers = () => {
		this.dropdownToggler.addEventListener("click", this.#toggleDropdownMenu);
		this.dropdownToggler.addEventListener("v3-popup.close", ({ detail: dropdown }) => {
			this.dropdownToggler.removeAttribute("aria-controls");
			dropdown.remove();
			dropdown.popperInstance?.destroy();
		});
	};

	#createDropdownMenu = () => {
		uniqueId(this.dropdownToggler, "v3-toolaction__drop-button");
		const dropdown = createDropdownMenu(this.button, this.dropdownToggler, this.options);
		this.dropdownToggler.setAttribute("aria-controls", dropdown.id);
	};

	#destroyDropdownMenu = () => {
		const dropdownId = this.dropdownToggler.getAttribute("aria-controls");
		const dropdown = dropdownId && document.getElementById(dropdownId);
		if (dropdown && !dropdown.matches(":focus-within")) {
			closePopup(dropdown);
		}
	};

	#toggleDropdownMenu = () => {
		if (this.dropdownToggler.getAttribute("aria-expanded") === "true") {
			this.#destroyDropdownMenu();
		} else if (!document.getElementById(this.dropdownToggler.getAttribute("aria-controls"))) {
			this.#createDropdownMenu();
		}
	};

	init = () => {
		const button = this.button;
		const defaultItem = this.options.menu.find(item => item.default);
		if (defaultItem) {
			const container = createElement("span", { class: "v3-toolaction v3-buttongroup" });
			const dropdownButton = createElement("button", {
					type: "button",
					form: button.getAttribute("form"),
					class: "v3-toolaction__drop js-auto-popup v3-button v3-popup-trigger v3-buttongroup__item-last ui-icon-only",
					"aria-haspopup": "true",
					"data-popper-placement": "bottom-end"
				},
				createElement("span", { class: "ui-icontext" }, "Show menu")
			);
			button.classList.add("v3-buttongroup__item-first");
			button.before(container);
			button.addEventListener("click", createOnButtonClickHandler(defaultItem.href, defaultItem.confirmation));
			container.replaceChildren(button, dropdownButton);
			this.dropdownToggler = dropdownButton;
		} else {
			button.classList.add("js-auto-popup", "v3-popup-trigger");
			button.setAttribute("aria-haspopup", "true");
			this.dropdownToggler = button;
		}
		this.#attachMenuHandlers();
		return this;
	};
}

const toolactionFactory = (button, options) => new ToolAction(button, options).init();

const toolaction = registerComponent(toolactionFactory);

function init(context, selector = ".v3-button[data-menu]") {
	return toolaction(selector, context);
}

export { toolaction, init };